import React, { useEffect } from 'react';
import { Button, Form, Input, Modal, Select } from 'antd';
import { useI18n } from '../i18n';
import { SERVICE_TYPES, PRICING_UNITS } from '../constants/productTypes';
import { isSupplierEligibleForLineType } from '../utils/supplierHelpers';

const { Option } = Select;

export default function ServiceItemModal({
  open,
  onCancel,
  onSubmit,
  editingItem = null,
  suppliers = [],
  supplierProducts = [],
}) {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const isEditing = Boolean(editingItem);
  const activeSuppliers = suppliers.filter((supplier) => (
    isSupplierEligibleForLineType(supplier, 'Service', supplierProducts)
    && (supplier.status !== 'Inactive' || supplier.id === editingItem?.supplierId)
  ));

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      code: editingItem?.code || '',
      name: editingItem?.name || '',
      supplierId: editingItem?.supplierId,
      serviceType: editingItem?.serviceType || 'Decoration',
      pricingUnit: editingItem?.pricingUnit || 'PerUnit',
    });
  }, [editingItem, form, open]);

  const handleFinish = async () => {
    const values = await form.validateFields();
    onSubmit({
      ...(editingItem || {}),
      code: values.code.trim(),
      name: values.name.trim(),
      supplierId: values.supplierId,
      serviceType: values.serviceType,
      pricingUnit: values.pricingUnit,
      status: editingItem?.status || 'Active',
    });
  };

  return (
    <Modal
      title={isEditing ? t('Edit Service') : t('Add Service')}
      open={open}
      onCancel={onCancel}
      width={520}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleFinish}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('Code')}
          name="code"
          rules={[{ required: true, message: t('Code is required') }]}
        >
          <Input placeholder={t('e.g. SRV-SCR')} />
        </Form.Item>
        <Form.Item
          label={t('Service Name')}
          name="name"
          rules={[{ required: true, message: t('Please enter service name') }]}
        >
          <Input placeholder={t('Service name')} />
        </Form.Item>
        <Form.Item
          label={t('Supplier')}
          name="supplierId"
          rules={[{ required: true, message: t('Please select supplier') }]}
        >
          <Select showSearch placeholder={t('Select supplier')} optionFilterProp="children">
            {activeSuppliers.map((supplier) => (
              <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={t('Service Type')}
          name="serviceType"
          rules={[{ required: true }]}
          extra={t('Decoration scales with order quantity; Charge is a one-off flat fee.')}
        >
          <Select>
            {SERVICE_TYPES.map((type) => (
              <Option key={type} value={type}>{t(type)}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={t('Pricing Unit')}
          name="pricingUnit"
          rules={[{ required: true }]}
        >
          <Select>
            {PRICING_UNITS.map((unit) => (
              <Option key={unit} value={unit}>{t(unit)}</Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}
